import React, { useState, useRef, useEffect } from 'react';
import { loglineApi } from '../../api/logline';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import LogLineHistory from '../LogLine/LogLineHistory';
import './LoglineSessionHeader.css';

/**
 * LoglineSessionHeader - Header for logline focus sessions
 *
 * Displays and manages the story's logline within a focus session context.
 * Provides edit, generate, history, and start over functionality.
 *
 * Props:
 * - storyId: UUID of the story
 * - currentLogline: Current logline text (or null if none)
 * - onLoglineUpdate: Callback when logline is updated (receives new logline text)
 * - onStartOver: Callback to trigger session start over flow
 */
export default function LoglineSessionHeader({
  storyId,
  currentLogline,
  onLoglineUpdate,
  onStartOver
}) {
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(currentLogline || '');
  const [isSaving, setIsSaving] = useState(false);

  // Generate state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  // History modal state
  const [showHistory, setShowHistory] = useState(false);

  // Start over confirmation state
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);

  const textareaRef = useRef(null);

  // Keep editText in sync with currentLogline when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditText(currentLogline || '');
    }
  }, [currentLogline, isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Edit handlers
  const handleEditClick = () => {
    setEditText(currentLogline || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditText(currentLogline || '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedText = editText.trim();
    if (!trimmedText) {
      handleCancel();
      return;
    }

    setIsSaving(true);
    try {
      await loglineApi.update(storyId, trimmedText, 'manual');
      onLoglineUpdate?.(trimmedText);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save logline:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Generate handler
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();

  const handleGenerate = async () => {
    const ok = await askPaid('logline');
    if (!ok) return;
    setIsGenerating(true);
    setGenerateError(null);

    try {
      const response = await loglineApi.generate(storyId);
      const generatedLogline = response?.data?.log_line || response?.log_line;

      if (generatedLogline) {
        // Update via API (this also creates history)
        await loglineApi.update(storyId, generatedLogline, 'generated');
        onLoglineUpdate?.(generatedLogline);
      }
    } catch (err) {
      console.error('Failed to generate logline:', err);
      setGenerateError('Failed to generate logline. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // History restore handler
  const handleHistoryRestore = (restoredLogline) => {
    onLoglineUpdate?.(restoredLogline);
    setShowHistory(false);
  };

  // Start over handlers
  const handleStartOverClick = () => {
    setShowStartOverConfirm(true);
  };

  const handleStartOverConfirm = () => {
    setShowStartOverConfirm(false);
    onStartOver?.();
  };

  const handleStartOverCancel = () => {
    setShowStartOverConfirm(false);
  };

  return (
    <div className="logline-session-header">
      {paidDialog}
      <div className="logline-session-header__content">
        <div className="logline-session-header__label">
          <span className="logline-session-header__icon">&#9733;</span>
          <span>Story Logline</span>
        </div>

        {isEditing ? (
          // Edit mode
          <div className="logline-session-header__edit">
            <textarea
              ref={textareaRef}
              className="logline-session-header__textarea"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your logline..."
              disabled={isSaving}
              rows={3}
            />
            <div className="logline-session-header__edit-actions">
              <button
                className="logline-session-header__btn logline-session-header__btn--secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="logline-session-header__btn logline-session-header__btn--primary"
                onClick={handleSave}
                disabled={isSaving || !editText.trim()}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          // Display mode
          <div className="logline-session-header__display">
            {currentLogline ? (
              <p className="logline-session-header__text">{currentLogline}</p>
            ) : (
              <p className="logline-session-header__empty">
                No logline yet. Your logline is the integration of what happens and what it means. Use the chat below to develop it, or click Edit to add one directly.
              </p>
            )}
          </div>
        )}

        {generateError && (
          <div className="logline-session-header__error">
            {generateError}
          </div>
        )}
      </div>

      {!isEditing && (
        <div className="logline-session-header__actions">
          <button
            className="logline-session-header__btn logline-session-header__btn--secondary"
            onClick={handleEditClick}
          >
            Edit
          </button>
          <button
            className="logline-session-header__btn logline-session-header__btn--primary"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
          <button
            className="logline-session-header__btn logline-session-header__btn--secondary"
            onClick={() => setShowHistory(true)}
          >
            History
          </button>
          <button
            className="logline-session-header__btn logline-session-header__btn--danger"
            onClick={handleStartOverClick}
          >
            Start Over
          </button>
        </div>
      )}

      {/* History Modal */}
      <LogLineHistory
        storyId={storyId}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        onRestore={handleHistoryRestore}
      />

      {/* Start Over Confirmation Modal */}
      {showStartOverConfirm && (
        <div className="logline-session-header__modal-overlay" onClick={handleStartOverCancel}>
          <div className="logline-session-header__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="logline-session-header__modal-title">Start Over?</h3>
            <p className="logline-session-header__modal-text">
              This will clear all messages in this session. Your logline and logline history will be preserved. This cannot be undone.
            </p>
            <div className="logline-session-header__modal-actions">
              <button
                className="logline-session-header__btn logline-session-header__btn--secondary"
                onClick={handleStartOverCancel}
              >
                Cancel
              </button>
              <button
                className="logline-session-header__btn logline-session-header__btn--danger"
                onClick={handleStartOverConfirm}
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Named export for flexibility
export { LoglineSessionHeader };
